// src/services/giftsApi.js

import { authorizedApi } from './index'; // Adjust the import path as necessary

export const giftsApi = authorizedApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Mutation to create a new gift.
     * Sends a POST request to /api/gifts/createGift with the gift details.
     */
    createGift: builder.mutation({
      query: (gift) => ({
        url: '/gifts/createGift',
        method: 'POST',
        body: gift,
        credentials: 'include',
      }),
      // Optionally, handle cache invalidation or updates here
      // For example, invalidating a "gifts" list query if you have one
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to update an existing gift.
     * Sends a PUT request to /api/gifts/:giftId with the updated gift details.
     */
    updateGift: builder.mutation({
      query: ({ giftId, ...gift }) => ({
        url: `/gifts/${giftId}`,
        method: 'PUT',
        body: gift,
      }),
      transformResponse: (response) => {
        // Update the cache with the new gift data
        return response;
      },
      invalidatesTags: ['Gifts'],
      // Add optimistic updates
      onQueryStarted: async (
        { giftId, ...gift },
        { dispatch, queryFulfilled },
      ) => {
        // Optimistically update the cache
        const patchResult = dispatch(
          giftsApi.util.updateQueryData('getGift', giftId, (draft) => {
            Object.assign(draft.gift, gift);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    /**
     * Mutation to prepare transactions for a gift (unified endpoint).
     * Sends a POST request to /api/gifts/:giftId/prepareTransactions.
     * Automatically routes to: HTLC (with/without swap), Email (with/without swap).
     */
    prepareTransactions: builder.mutation({
      query: (giftId) => ({
        url: `/gifts/${giftId}/prepareTransactions`,
        method: 'POST',
      }),
    }),

    /**
     * Mutation to prepare HTLC claim transaction.
     * Sends a POST request to /api/gifts/:giftId/prepareHTLCClaim.
     */
    prepareHTLCClaim: builder.mutation({
      query: ({ giftId, claimCode, sourceWallet }) => ({
        url: `/gifts/${giftId}/prepareHTLCClaim`,
        method: 'POST',
        body: { claimCode, sourceWallet },
      }),
    }),

    /**
     * Mutation to claim HTLC gift.
     * Sends a POST request to /api/gifts/:giftId/claimHTLC.
     */
    broadcastClaimHTLC: builder.mutation({
      query: ({ giftId, claimCode, signedTransaction, sourceWallet }) => ({
        url: `/gifts/${giftId}/claimHTLC`,
        method: 'POST',
        body: { claimCode, signedTransaction, sourceWallet },
      }),
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to track the transfer status of a gift.
     * Sends a POST request to /api/gifts/trackTransfer with giftId and transactionHash.
     */
    trackTransfer: builder.mutation({
      query: (transfer) => ({
        url: '/gifts/trackTransfer',
        method: 'POST',
        body: transfer,
      }),
      // Optionally, handle cache invalidation or updates here
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to broadcast and track a gift transfer transaction (unified endpoint).
     * Sends a POST request to /api/gifts/broadcastAndTrack.
     * Automatically routes to: HTLC (with/without swap), Email (with/without swap).
     * Parameters:
     *   - giftId (required)
     *   - serializedTransaction (required) - First transaction (swap TX if with swap, otherwise the main TX)
     *   - serializedSecondTransaction (optional) - Second transaction for swap gifts (HTLC or transfer TX)
     *   - requestId (optional) - Jupiter Ultra request ID for swap gifts
     */
    broadcastAndTrack: builder.mutation({
      query: ({ giftId, serializedTransaction, serializedSecondTransaction, requestId }) => ({
        url: `gifts/${giftId}/broadcastAndTrack`,
        method: 'POST',
        body: {
          giftId,
          serializedTransaction,
          ...(serializedSecondTransaction && { serializedSecondTransaction }),
          ...(requestId && { requestId }),
        },
      }),
      invalidatesTags: ['Gifts'],
    }),

    getGift: builder.query({
      query: (giftId) => `/gifts/${giftId}`,
      providesTags: (result, error, giftId) => [{ type: 'Gifts', id: giftId }],
    }),
    getGifts: builder.query({
      query: () => '/gifts', // calls GET /api/gifts
      providesTags: ['Gifts'],
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in functional components
export const {
  useCreateGiftMutation,
  useUpdateGiftMutation,
  usePrepareTransactionsMutation,
  usePrepareHTLCClaimMutation,
  useBroadcastClaimHTLCMutation,
  useTrackTransferMutation,
  useGetGiftQuery,
  useLazyGetGiftQuery,
  useGetGiftsQuery,
  useBroadcastAndTrackMutation,
} = giftsApi;
